/**
 * Filter vocabulary, URL serialisation and display formatting for dealer stock.
 *
 * Everything here mirrors the marketing site's /dealer-stock so the two
 * surfaces offer the same choices and produce the same query strings:
 *   colour buckets  <- magic-motorsports/src/components/DealerStockFilter.jsx
 *   mileage steps   <- same file (fixed steps, deliberately not free text)
 *   sort options    <- magic-motorsports/src/pages/DealerStock.jsx
 *   URL parameters  <- same file's location.search reader/writer
 */

import { normalizeTransmission, translateBodyType } from './vehicleNames.js';

/** The 12 filter keys /api/cars understands, in sidebar order. */
export const EMPTY_FILTERS = {
  make: '',
  model: '',
  minYear: '',
  maxYear: '',
  color: '',
  transmission: '',
  bodyType: '',
  chassisGeneration: '',
  minPrice: '',
  maxPrice: '',
  minMileage: '',
  maxMileage: '',
};

export const SORT_OPTIONS = [
  { value: 'price-low-high', label: 'Price: Low to High' },
  { value: 'price-high-low', label: 'Price: High to Low' },
  { value: 'year-new-old', label: 'Year: Newest First' },
  { value: 'year-old-new', label: 'Year: Oldest First' },
  { value: 'mileage-low-high', label: 'Mileage: Low to High' },
  { value: 'mileage-high-low', label: 'Mileage: High to Low' },
];

export const DEFAULT_SORT = 'price-low-high';

// The marketing site offers 20/50/100 and defaults to 20. Matching that default
// matters more than it looks: neither site can sort across pages (there is no
// server-side sort), so the page size decides how many cars the sort can even
// see — a different default makes the same URL show a different first car.
// 12 stays as an extra option because it fills the portal's 3-column grid.
export const PAGE_SIZE_OPTIONS = [12, 20, 50, 100];
export const DEFAULT_PAGE_SIZE = 20;

/**
 * 0 - 200,000 km in 10k steps.
 *
 * Free-text mileage boxes read as a lie here: carlistings stores dealer-stock
 * mileage in THOUSANDS of km, so typing 10 into a box labelled "Mileage (km)"
 * filtered at 10,000 km. Fixed steps state the real number and /api/cars scales
 * it back to the stored unit (it divides by 1000 for carlistings and by 1 for
 * currentInventory). Same reasoning, same steps, as the marketing site.
 */
export const MILEAGE_STEPS = Array.from({ length: 21 }, (_, i) => i * 10000);

/**
 * Raw feed colours -> the 13 buckets the filter offers.
 *
 * The feed's `color` field is free text in two languages ("Pearl White",
 * "ガンメタリック", "Color Black"), so a distinct() over it yields hundreds of
 * one-off values that are useless as filter options. /api/cars resolves a
 * bucket NAME back to a regex over its keywords, so the bucket name is what
 * gets sent as ?color=. Keywords copied verbatim from the marketing site — the
 * backend's table has to agree with this one or a bucket returns nothing.
 */
const COLOR_BUCKETS = {
  Black: ['Black', '黒', 'クロ', 'ブラック'],
  White: ['White', '白', 'シロ', 'ホワイト', 'Pearl White', 'Pure White', 'Sophia White', 'Super White'],
  'Gray/Silver': ['Gray', 'Silver', 'グレー', 'シルバー', 'Gun Metallic', 'Light Silver'],
  Red: ['Red', '赤', 'アカ', 'レッド'],
  Blue: ['Blue', '青', 'Navy', 'Light Blue', 'Dark Blue', 'ブルー', 'ネイビー'],
  Green: ['Green', '緑', 'ミドリ', 'グリーン', 'Dark Green'],
  Yellow: ['Yellow', '黄', 'イエロー'],
  Orange: ['Orange', '橙', 'オレンジ'],
  Brown: ['Brown', '茶', 'ブラウン'],
  Purple: ['Purple', '紫', 'パープル'],
  Pink: ['Pink', '桃', 'ピンク'],
  Gold: ['Gold', '金', 'ゴールド'],
  Beige: ['Beige', 'ベージュ', 'アイボリー', 'Ivory'],
};

const bucketForColor = (colorName) => {
  const lower = String(colorName || '').toLowerCase();
  return (
    Object.keys(COLOR_BUCKETS).find((bucket) =>
      COLOR_BUCKETS[bucket].some((keyword) => lower.includes(keyword.toLowerCase()))
    ) || null
  );
};

/**
 * Build the Color dropdown from the raw feed values: the buckets actually
 * present, A-Z, plus a "Misc" option when some feed colour matched none of
 * them (the backend turns Misc into an inverse match over every keyword).
 */
export const getConsolidatedColors = (colors = []) => {
  const present = new Set();
  let hasMisc = false;

  colors.forEach((color) => {
    const bucket = bucketForColor(color);
    if (bucket) present.add(bucket);
    else if (color) hasMisc = true;
  });

  const options = [...present].sort().map((color) => ({ label: color, value: color }));
  if (hasMisc) options.push({ label: 'Misc', value: 'Misc' });
  return options;
};

/**
 * Body-type options: the option VALUE stays the raw Japanese feed string
 * (that is what /api/cars matches on), while the LABEL is the English
 * translation. Sorted by the label the user reads, not the value.
 *
 * translateBodyType falls through to the original string for anything it does
 * not recognise, so an unmapped body type is still selectable rather than
 * rendering as a blank option — it only returns '' for the feed's "その他"
 * catch-all, which is dropped here for exactly that reason.
 */
export const getBodyTypeOptions = (bodyTypes = []) =>
  bodyTypes
    .map((value) => ({ value, label: translateBodyType(value) || '' }))
    .filter((option) => option.value && option.label)
    .sort((a, b) => a.label.localeCompare(b.label));

// ---------------------------------------------------------------------------
// Display formatting
// ---------------------------------------------------------------------------

/**
 * Mileage, in the unit the row is actually stored in.
 *
 * carlistings stores THOUSANDS of km and currentInventory stores real km —
 * confirmed against server.js, which divides the filter bounds by 1000 and 1
 * respectively. So "169" on a scraped listing means 169,000 km and prints as
 * "169k km", while the same number on curated inventory means 169 km.
 */
export const formatMileage = (car) => {
  if (!car || !car.mileage) return 'N/A';
  return car.source === 'current-inventory' ? `${car.mileage} km` : `${car.mileage}k km`;
};

/** Real kilometres, for sorting across the two units. */
export const mileageInKm = (car) => {
  if (!car || !car.mileage) return 0;
  const n = Number(car.mileage) || 0;
  return car.source === 'current-inventory' ? n : n * 1000;
};

/** Transmission in English ("AT4速" -> "4-speed AT"); '' when unpublishable. */
export const formatTransmission = (car) => normalizeTransmission(car && car.transmission) || '';

/** Body type in English ("軽自動車" -> "Kei Car"); '' for the その他 catch-all. */
export const formatBodyType = (car) => translateBodyType(car && car.bodyType) || '';

/** The feed prefixes some colours with a literal "Color " — drop it. */
export const formatColor = (car) =>
  car && car.color ? String(car.color).replace(/Color\s+/, '') : '';

/**
 * Sale state. currentInventory carries a real four-value status; carlistings
 * only distinguishes 'active' from everything else.
 *
 * Neither site requests includeSold=true, so /api/cars returns active dealer
 * stock only and this almost never fires on the grid. It matters for a direct
 * link to a listing that has since sold, which would otherwise render as
 * purchasable.
 *
 * 'duplicate' is not a sale: the scraper retired that record because the same
 * car is listed under another id. fetchDealerStockById swaps in the kept copy,
 * whose status is the real one; this covers a snapshot stored with the flag and
 * a kept copy that could not be read.
 */
export const saleStateFor = (car) => {
  if (!car) return { sold: false, badge: null };
  if (car.source === 'current-inventory') {
    if (car.status === 'sold') return { sold: true, badge: 'SOLD' };
    if (car.status === 'reserved') return { sold: false, badge: 'RESERVED' };
    if (car.status === 'incoming') return { sold: false, badge: 'INCOMING' };
    return { sold: false, badge: null };
  }
  return car.status && !['active', 'duplicate'].includes(car.status)
    ? { sold: true, badge: 'SOLD' }
    : { sold: false, badge: null };
};

// ---------------------------------------------------------------------------
// URL <-> state
// ---------------------------------------------------------------------------

/**
 * Read filters, search, sort, page and page size out of a location.search.
 *
 * `?year=1995` is accepted as a shorthand for minYear=maxYear=1995 because the
 * marketing site's make/model pages link that way and those links get shared.
 */
const NUMERIC_FILTER_KEYS = new Set([
  'minYear', 'maxYear', 'minPrice', 'maxPrice', 'minMileage', 'maxMileage',
]);

export const readStateFromSearch = (search) => {
  const params = new URLSearchParams(search || '');
  const filters = { ...EMPTY_FILTERS };

  // Numeric filters are validated, not trusted. ?minPrice=abc used to reach
  // describeActiveFilters and render a "$NaN" chip, and was forwarded to the
  // API as a garbage bound.
  Object.keys(EMPTY_FILTERS).forEach((key) => {
    const value = params.get(key);
    if (value === null || value === '') return;
    if (NUMERIC_FILTER_KEYS.has(key)) {
      const n = Number(value);
      if (!Number.isFinite(n) || n < 0) return;
      filters[key] = String(n);
      return;
    }
    filters[key] = value;
  });

  // ?year=1995 is shorthand for minYear=maxYear=1995 — the marketing site's
  // make/model pages link that way. An explicit minYear/maxYear in the same URL
  // is the more specific statement, so it wins, matching that site's precedence.
  const year = Number(params.get('year'));
  if (Number.isFinite(year) && year > 0) {
    if (!filters.minYear) filters.minYear = String(year);
    if (!filters.maxYear) filters.maxYear = String(year);
  }

  const pageSize = parseInt(params.get('limit'), 10);
  const page = parseInt(params.get('page'), 10);
  const sortParam = params.get('sort');

  return {
    filters,
    search: params.get('search') || '',
    sortOrder: SORT_OPTIONS.some((o) => o.value === sortParam) ? sortParam : DEFAULT_SORT,
    page: Number.isFinite(page) && page > 0 ? page : 1,
    pageSize: PAGE_SIZE_OPTIONS.includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE,
  };
};

/**
 * The inverse: a canonical query string for the current state. Defaults are
 * omitted so a pristine grid has a clean `/dealer-stock` URL rather than one
 * carrying page=1&limit=12&sort=price-low-high.
 */
export const writeStateToSearch = ({ filters = {}, search = '', sortOrder = DEFAULT_SORT, page = 1, pageSize = DEFAULT_PAGE_SIZE } = {}) => {
  const params = new URLSearchParams();

  Object.keys(EMPTY_FILTERS).forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== '') params.append(key, String(value));
  });

  if (search) params.append('search', search);
  if (sortOrder && sortOrder !== DEFAULT_SORT) params.append('sort', sortOrder);
  if (page && page > 1) params.append('page', String(page));
  if (pageSize && pageSize !== DEFAULT_PAGE_SIZE) params.append('limit', String(pageSize));

  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

/** Human-readable chips for the applied-filters row. */
export const describeActiveFilters = (filters = {}, searchQuery = '') => {
  const chips = [];
  const push = (key, label) => chips.push({ key, label });

  if (searchQuery) push('search', `"${searchQuery}"`);
  if (filters.make) push('make', filters.make);
  if (filters.model) push('model', filters.model);

  if (filters.minYear && filters.maxYear) {
    push(
      'year',
      filters.minYear === filters.maxYear ? filters.minYear : `${filters.minYear}-${filters.maxYear}`
    );
  } else if (filters.minYear) push('minYear', `From ${filters.minYear}`);
  else if (filters.maxYear) push('maxYear', `Up to ${filters.maxYear}`);

  if (filters.color) push('color', filters.color);
  if (filters.transmission) push('transmission', filters.transmission === 'Auto' ? 'Automatic' : 'Manual');
  if (filters.bodyType) push('bodyType', translateBodyType(filters.bodyType) || filters.bodyType);
  if (filters.chassisGeneration) push('chassisGeneration', filters.chassisGeneration);

  const money = (n) => `$${Number(n).toLocaleString()}`;
  if (filters.minPrice && filters.maxPrice) push('price', `${money(filters.minPrice)} - ${money(filters.maxPrice)}`);
  else if (filters.minPrice) push('minPrice', `From ${money(filters.minPrice)}`);
  else if (filters.maxPrice) push('maxPrice', `Up to ${money(filters.maxPrice)}`);

  const km = (n) => `${Number(n).toLocaleString()} km`;
  if (filters.minMileage && filters.maxMileage) push('mileage', `${km(filters.minMileage)} - ${km(filters.maxMileage)}`);
  else if (filters.minMileage) push('minMileage', `From ${km(filters.minMileage)}`);
  else if (filters.maxMileage) push('maxMileage', `Up to ${km(filters.maxMileage)}`);

  return chips;
};

/** Which state keys a chip clears when dismissed. */
export const FILTER_CHIP_KEYS = {
  year: ['minYear', 'maxYear'],
  price: ['minPrice', 'maxPrice'],
  mileage: ['minMileage', 'maxMileage'],
};
